import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';

// The trace is the light. A rig would be a lamp pointed at a cathode tube.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'scope3d',
  family: 'vector',
  danceability: 0.62,
  energyLevel: 0.58,
  bpmMin: 80,
  bpmMax: 160,
  movement: 0.65,
  tonality: 0.70,
  prefers: { gridStrength: [0.55, 1.0] },
  enabled: true
};

// Scope3D: a three-dimensional Lissajous figure drawn by a moving spot with
// phosphor persistence, its frequencies whole-number ratios of the tempo.
//
// WHAT SEPARATES IT FROM PHOSPHORSCOPE. There the trace is on a flat screen and
// the figure is a DRAWING. Here it is a sculpture, and two things follow that
// you cannot get in two dimensions:
//   DEPTH       as the camera orbits, parts of the curve pass behind others and
//               the knot resolves itself.
//   PRECESSION  when two frequencies sit near a whole-number ratio without
//               reaching it, the figure turns on its own axis with nothing
//               rotating it. Nothing in this file rotates the curve - the only
//               thing that moves is a phase drifting by four thousandths of a
//               radian a frame. ?scopefreeze=1 stops the camera so you can
//               watch it happen on its own.
//
// A POINT, NOT A LINE. An oscilloscope does not draw a curve; a spot runs and
// the phosphor remembers where it has been for a moment. Everything below
// follows from that - the short persistence, the bright head, and the fact that
// the figure is almost never all visible at once.

// --- the curve ---------------------------------------------------------------
const AMP = 2.15;                 // half-extent of the figure, world units
const AMP_LOW = 0.15;             // bands.low breathes it, +-15%
const PHI_DRIFT = 0.004;          // radians per frame on z - the precession
const PHI_DRIFT_MID = 1.8;        // bands.mid scales it
const FREQ_POOL_EARLY = [1, 2, 3];
const FREQ_POOL_MID = [1, 2, 3, 4, 5];

// --- the spot ----------------------------------------------------------------
// One full traversal per BAR. Everything else is derived from this: the base
// angular rate is 2*PI*bpm/60/4, so the parameter advances 2*PI over four
// beats and every whole-number multiple of it closes at the same instant.
// 2400, which is also the tier-2 ring size - so the ring holds exactly one
// traversal. 1200 was too few for an eight-pixel halo: in the fast parts of the
// curve consecutive samples land 6.8 px apart, and a quad 8 px wide every 6.8
// px reads as a string of beads rather than as a line. Halving the step puts
// them 3.4 px apart and the halo bridges them.
const SAMPLES_PER_BAR = 2400;
const HEAD_SAMPLES = 40;
// 400 ms, where a real P31 is dark again in half that. The authentic number
// does not survive the arithmetic: the spot covers the whole figure in one BAR,
// which is two seconds at 120 bpm, so 180 ms leaves 9% of the figure lit behind
// the head - a short arc with no curvature in it, from which the shape cannot
// be read at all. 400 ms shows about 20%, which is enough to see where the
// curve is going without the figure settling into a static wire.
const PERSIST_MS = 400;
const PERSIST_MS_SECOND = 650;
const PERSIST_MS_DROP = 1200;
const DROP_BARS = 2;

// TWO WIDTHS PER STATE, not one. The beam has a hot narrow core inside a wide
// soft glow, and drawing the quad at the CORE's width leaves no room for the
// glow at all: at a three-pixel quad the halo has a pixel and a half either
// side of centre to fall off in, so what reaches the screen is a one-pixel
// line with a faint fringe. The quad has to be the HALO's width and the core
// lives inside it.
const TRAIL_CORE_PX = 3.0;
const TRAIL_HALO_PX = 8.0;
const HEAD_CORE_PX = 6.0;
const HEAD_HALO_PX = 14.0;
const HEAD_PX_HIGH = 3.0;         // bands.high widens the head
const SECOND_CORE_PX = 2.0;
const SECOND_HALO_PX = 5.0;
// A tube whose three guns are slightly out of register. Half a pixel, which is
// the difference between "an old monitor" and "a broken one".
const CONVERGE_PX = 0.5;

// P31 - the green every oscilloscope screen has ever been. NEVER the palette:
// the palette gets the second channel, which is the whole point of having one.
const P31 = [0.36, 1.0, 0.44];
const P31_CORE = 0.72;            // how far the hot core goes toward white
const PEAK_LUM = 0.92;
// What the trace is worth AT THE HEAD, before the exponential takes it down.
const BASE_LUM = 0.85;

// --- the camera --------------------------------------------------------------
const ORBIT_RADIUS = 6.0;
const ORBIT_BEATS = 32;
const TILT_DEG = 30;
const TILT_PERIOD_S = 47;
// The figure sits 15% of the frame height above centre, so the cover has the
// middle and the curve is not permanently behind it.
const AIM_DROP = 0.15;

// --- the graticule -----------------------------------------------------------
// 2.6, not 3.2: the graticule is there to give the figure a scale, and a box
// half again as big as the figure reads as a room the figure is lost in.
// 10 divisions with every fifth marked, which is how a scope screen is ruled.
const GRID_EXTENT = 2.6;
const GRID_DIV = 10;
// 0.07 nominal for 4% on screen. The brief's number is what should ARRIVE, and
// what arrives is less: the safe zone takes 45% of it across the middle of the
// frame, and a one-pixel line only reaches full value on the pixels it passes
// through dead centre. Measured at 0.04 the graticule delivered 1.4%, which is
// below what a dark-adapted eye picks out on a black field.
const GRID_LEVEL = 0.07;

// MEASURED ON THE PI, at 1280x400 - the resolution its two panels actually
// are. Median frame, forced to completion with a readPixels:
//
//   tier   samples x traces   visualizer   + composer
//    2        2400 x 2          14.1 ms      31.8 ms
//    1        1400 x 2           7.8 ms      22.3 ms
//    0         900 x 1           3.4 ms      16.1 ms
//
// THE UPLOAD IS NOT FREE, which the brief assumes it is. Re-measured at tier 2
// with the attribute uploads suppressed, the visualizer drops from 14.1 ms to
// 10.9 - so pushing two ring buffers of positions across the bus every frame,
// and filling them, is 3.2 ms, near a quarter of its own time. That is also
// why the per-segment BRIGHTNESS is not uploaded with them: the age of every
// segment changes every frame, and sending that too would have doubled the
// traffic. It is derived in the shader from the ring index and one uniform.
//
// Tier 2 lands at 31 fps here, so the frame-time governor will settle the Pi
// on tier 1 or 0 - which is what it is for. Widening the trace from a 3 px
// quad to an 8 px one and doubling the sample rate, both of which the trace
// needed to be legible at all, cost 4.4 ms of the tier-2 figure.
const TIER = {
  0: { samples: 900, second: false, grids: false, converge: false },
  1: { samples: 1400, second: true, grids: true, converge: false },
  2: { samples: 2400, second: true, grids: true, converge: true }
};
const DEFAULT_TIER = 2;

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function lerp(a, b, t) { return a + (b - a) * t; }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}
function gcd(a, b) { while (b) { const t = a % b; a = b; b = t; } return a; }

// =============================================================================
// The trace.
//
// HIGHP, not the mediump the brief asks for, and for the same reason Vertigo
// needs it: a fat line is expanded by taking the DIFFERENCE of its two
// endpoints in SCREEN PIXELS, and on the TV mediump is genuinely fp16, where
// the spacing at 1000 px is a whole pixel. The direction vector would quantise
// to the axes and every segment would be a staircase. The fragment shader,
// which only ever sees small numbers, is mediump as asked.
// =============================================================================
const traceVertex = `
  precision highp float;

  attribute vec3 aStart;
  attribute vec3 aEnd;
  attribute float aIdx;      // this segment's place in the ring

  uniform vec2 uHalfRes;
  uniform float uHead;       // ring index of the newest complete segment
  uniform float uCount;      // ring size
  uniform float uTauSamples; // persistence, in samples
  uniform float uAgeBias;    // added to every age while paused - see below
  uniform float uHoldHead;   // and what keeps the stopped spot alight
  uniform float uHeadLen;
  uniform float uTrailCore;
  uniform float uTrailHalo;
  uniform float uHeadCore;
  uniform float uHeadHalo;
  uniform float uOpacity;
  uniform float uBaseLum;

  varying float vSide;
  varying float vBright;
  varying float vHead;
  varying float vCoreFrac;   // where the core ends, in vSide units
  varying float vAA;         // and one screen pixel, in the same units
  varying vec4 vClip;

  void main() {
    // AGE IS COMPUTED, NOT STORED. The ring index is written once when the
    // sample is taken and never touched again; the age of every segment
    // changes every frame, and deriving it from one uniform is what lets the
    // whole trail be re-lit without uploading anything.
    float rawAge = mod(uHead - aIdx + uCount, uCount);
    // PAUSE DOES NOT FREEZE THE PHOSPHOR. The spot stops, but the screen goes
    // on forgetting - which is what a tube does when the signal is cut, and
    // what the brief asks for. Ages are relative to the head, and the head
    // stops moving when the spot does, so nothing would decay at all without
    // this bias: the trail would simply hang there, which is the one reading
    // the brief rules out.
    float age = rawAge + uAgeBias;
    float fade = exp(-age / uTauSamples);
    // AND A WINDOW ON TOP OF THE EXPONENTIAL. An exponential never actually
    // reaches zero: at three and a half time constants the trail is down to
    // 3% of the head, which is still a visible thread, and with a ring that
    // holds exactly one traversal that thread closes the loop - the figure is
    // then permanently all there, which is the state the brief reserves for
    // drops. Ramping it out from 2.5 tau, where it is already at 8%, removes
    // the ghost without putting a visible end on the trail.
    fade *= 1.0 - smoothstep(2.5, 3.6, age / uTauSamples);
    // The head: the spot itself, wider and hotter, so it is seen running - and
    // the last thing left on a stopped screen, because the beam is still
    // sitting on it.
    float head = 1.0 - smoothstep(0.0, uHeadLen, rawAge);
    vHead = head;
    // THE TRACE STARTS AT FULL BRIGHTNESS AND DECAYS FROM THERE. It was
    // starting at uOpacity and being multiplied down by the profile on top, so
    // by the time it had decayed at all there was nothing left to see.
    vBright = (fade + head * uHoldHead) * uOpacity * uBaseLum;

    // The quad is the halo's width; the core is a fraction of it. Both in
    // PIXELS, converted to clip space below through uHalfRes - never in world
    // units, or a line would thin out with distance like a wire instead of
    // holding its weight like a beam.
    float px = mix(uTrailHalo, uHeadHalo, head);
    float corePx = mix(uTrailCore, uHeadCore, head);
    vCoreFrac = clamp(corePx / max(px, 0.001), 0.05, 0.95);
    // One pixel of softening on the core's edge: vSide spans -1..1 over px
    // pixels, so a pixel is 2/px, and half of that each side of the threshold.
    vAA = clamp(1.0 / max(px, 1.0), 0.01, 0.4);

    vec4 cs = projectionMatrix * modelViewMatrix * vec4(aStart, 1.0);
    vec4 ce = projectionMatrix * modelViewMatrix * vec4(aEnd, 1.0);
    // Behind the lens there is no screen position; collapse rather than fold.
    float ok = step(0.02, cs.w) * step(0.02, ce.w);
    // The one segment that joins the newest sample to the oldest is a chord
    // across the whole figure. It is always the oldest, so it is already dark,
    // but it is collapsed outright rather than left to the exponential.
    ok *= step(0.5, age) * step(age, uCount - 1.5);

    vec2 ss = cs.xy / max(cs.w, 0.02) * uHalfRes;
    vec2 se = ce.xy / max(ce.w, 0.02) * uHalfRes;
    vec2 d = se - ss;
    float len = max(length(d), 1e-4);
    vec2 dir = d / len;
    vec2 nrm = vec2(-dir.y, dir.x);

    vSide = position.y;
    vec4 clip = mix(cs, ce, position.x);
    float w = max(clip.w, 0.02);
    // Half a pixel of cap on each end, so consecutive segments join without a
    // gap where the curve turns sharply.
    // A QUARTER OF THE WIDTH OF CAP, not half. The cap is there so consecutive
    // quads meet at a corner rather than leaving a notch; at half the width it
    // makes each segment nearly square, and squares laid end to end down a
    // curve are what the beading was.
    vec2 off = nrm * (position.y * px * 0.5)
             + dir * ((position.x * 2.0 - 1.0) * px * 0.25);
    clip.xy += off / uHalfRes * w;
    clip.xyz *= ok;
    clip.w = mix(1.0, clip.w, ok);
    vClip = clip;
    gl_Position = clip;
  }
`;

const traceFragment = `
  precision mediump float;

  uniform vec3 uColor;
  uniform float uConverge;   // pixels of red/blue misregistration
  uniform float uCore;

  uniform float uAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;

  varying float vSide;
  varying float vBright;
  varying float vHead;
  varying float vCoreFrac;
  varying float vAA;
  varying vec4 vClip;

  // Scaling by one factor keeps the hue; clamping per channel collapses a
  // saturated colour toward white the moment two channels hit the ceiling.
  vec3 clampLum(vec3 c) {
    float mx = max(c.r, max(c.g, c.b));
    return mx > ${PEAK_LUM.toFixed(2)} ? c * (${PEAK_LUM.toFixed(2)} / mx) : c;
  }

  // The beam's cross-section, as TWO zones rather than one curve: a hot core
  // with a soft edge, sitting inside a wide phosphor glow. Returning one
  // number for both is what collapsed the trace to a hairline - the core and
  // the halo want different colours as well as different widths.
  float coreAt(float s) {
    return 1.0 - smoothstep(vCoreFrac - vAA, vCoreFrac + vAA, abs(s));
  }
  float haloAt(float s) {
    return pow(1.0 - smoothstep(0.0, 1.0, abs(s)), 1.6);
  }

  void main() {
    if (vBright <= 0.002) discard;

    // A TUBE WHOSE GUNS ARE OUT OF REGISTER. Half a pixel each way, sampled as
    // three offsets of the same profile rather than as three draw calls - the
    // red edge and the blue edge fall on opposite sides of the trace.
    float o = uConverge * vAA * 2.0;
    vec3 coreC = vec3(coreAt(vSide - o), coreAt(vSide), coreAt(vSide + o));
    vec3 haloC = vec3(haloAt(vSide - o), haloAt(vSide), haloAt(vSide + o));

    // The core is near-white green at all times, not only at the head; the
    // halo is the phosphor's own colour. The head differs by being WIDER and
    // brighter, which is what makes it read as the spot rather than as a
    // change of material.
    vec3 hot = mix(uColor, vec3(1.0), uCore);
    vec3 col = (hot * coreC * (0.85 + 0.55 * vHead)
              + uColor * haloC * 0.55) * vBright;

    vec2 ndc = vClip.xy / max(vClip.w, 0.0001);
    vec2 sp = vec2(ndc.x * uAspect, ndc.y);
    vec2 q = abs(sp - uSafeCenter) - uSafeHalfSize;
    float sd = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    col *= mix(0.45, 1.0, smoothstep(0.0, uSafeFeather, sd));

    gl_FragColor = vec4(clampLum(col), 1.0);
  }
`;

// =============================================================================
// The graticule: three faint planes, which is what turns a drawing into a
// volume. Without them the figure floats in nothing and its depth is a guess.
// =============================================================================
const gridVertex = `
  precision highp float;
  uniform float uFocalPx;
  uniform float uExtent;
  varying vec2 vUv;
  varying vec4 vClip;
  varying float vUvPerPx;
  void main() {
    vUv = uv;
    vec4 mv = modelViewMatrix * vec4(position, 1.0);
    vClip = projectionMatrix * mv;
    gl_Position = vClip;
    // HOW MUCH UV ONE SCREEN PIXEL IS WORTH, HERE. This is the whole reason
    // the graticule came out as scattered dashes rather than as three grids:
    // a single width for the whole plane is right at one depth and wrong
    // everywhere else, so the near half of each plane drew fat lines and the
    // far half drew lines thinner than a pixel, which sample into dots. The
    // plane spans 2*uExtent world units per unit of uv and is uFocalPx/depth
    // pixels per world unit, so this is exact - and it needs no derivatives.
    float depth = max(-mv.z, 0.05);
    vUvPerPx = depth / max(uFocalPx * 2.0 * uExtent, 1.0);
  }
`;
const gridFragment = `
  precision mediump float;
  uniform vec3 uColor;
  uniform float uLevel;
  uniform float uDiv;

  uniform float uAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;

  varying vec2 vUv;
  varying vec4 vClip;
  varying float vUvPerPx;

  // One ruling, one pixel wide wherever it happens to be on screen. w is the
  // uv value of a pixel at this fragment's own depth.
  float rule(float x, float n, float w) {
    float p = x * n;
    float d = abs(fract(p) - 0.5) / n;          // distance to the nearest line
    return 1.0 - smoothstep(0.0, w, d);
  }

  void main() {
    float w = max(vUvPerPx, 1e-6);
    // Minor divisions, then every fifth one marked, then the two centre axes -
    // which is how a graticule is actually printed.
    float minor = max(rule(vUv.x, uDiv, w), rule(vUv.y, uDiv, w));
    float major = max(rule(vUv.x, uDiv / 5.0, w), rule(vUv.y, uDiv / 5.0, w));
    float axis = max(1.0 - smoothstep(0.0, w * 1.2, abs(vUv.x - 0.5)),
                     1.0 - smoothstep(0.0, w * 1.2, abs(vUv.y - 0.5)));
    float a = minor * uLevel + major * uLevel * 1.4 + axis * uLevel * 2.0;
    if (a <= 0.0015) discard;

    vec2 ndc = vClip.xy / max(vClip.w, 0.0001);
    vec2 sp = vec2(ndc.x * uAspect, ndc.y);
    vec2 q = abs(sp - uSafeCenter) - uSafeHalfSize;
    float sd = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    a *= mix(0.45, 1.0, smoothstep(0.0, uSafeFeather, sd));

    gl_FragColor = vec4(uColor * a, 1.0);
  }
`;

export class Scope3DVisualizer extends Visualizer {
  constructor(options) {
    super();
    const tier = options && options.qualityTier != null ? options.qualityTier : DEFAULT_TIER;
    this._tier = TIER[tier] ? tier : DEFAULT_TIER;
    this._freeze = urlParam('scopefreeze') === '1';
    const abc = urlParam('scopeabc');
    this._forcedABC = null;
    if (abc) {
      const p = abc.split(',').map(function (x) { return parseInt(x, 10); });
      if (p.length === 3 && p.every(function (n) { return Number.isFinite(n) && n >= 1 && n <= 9; })) {
        this._forcedABC = p;
      }
    }
    const pm = parseFloat(urlParam('scopepersist'));
    this._forcedPersist = Number.isFinite(pm) && pm > 10 ? pm : null;
  }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    const layout = TIER[this._tier];
    this._layout = layout;
    const N = layout.samples;
    this._n = N;

    this._shared = Object.assign({}, safeZoneUniforms, {
      uHalfRes: { value: new THREE.Vector2(640, 360) }
    });

    this._traces = [];
    this._traces.push(this._makeTrace(scene, N, P31,
      [TRAIL_CORE_PX, TRAIL_HALO_PX, HEAD_CORE_PX, HEAD_HALO_PX], PERSIST_MS, 1.0));
    if (layout.second) {
      this._traces.push(this._makeTrace(scene, N, [0.4, 0.7, 1.0],
        [SECOND_CORE_PX, SECOND_HALO_PX, SECOND_CORE_PX * 1.8, SECOND_HALO_PX * 1.8],
        PERSIST_MS_SECOND, 0.70));
    }

    if (layout.grids) this._makeGrids(scene);

    // --- state ---
    this._time = 0;
    this._paused = false;
    this._t = 0;                  // the curve parameter
    this._phi = [0, 0, 0];
    this._abc = [1, 2, 3];
    this._abc2 = [2, 2, 3];
    this._lastPhrase = null;
    this._lastBar = null;
    this._beats = 0;
    this._beatOffset = 0;
    this._lastRealBeats = null;
    this._orbit = 0;
    this._orbitTarget = null;
    this._tiltPhase = 0;
    this._lowSmooth = 0.5;
    this._midSmooth = 0.5;
    this._highSmooth = 0.5;
    this._dropUntil = -1;
    this._pauseAge = 0;
    this._pendingDropAt = null;
    this._lastPaletteRef = undefined;
    this._aim = new THREE.Vector3();

    this._pickFreqs(0.5);
    this._placeCamera(0, 120);
  }

  // One trace: a ring of segments, expanded to a fat line in the shader. The
  // per-instance data is written ONCE per sample and the whole trail is re-lit
  // every frame from a single uniform - see the age computation in the shader.
  _makeTrace(scene, N, color, widths, persistMs, opacity) {
    const quad = new THREE.BufferGeometry();
    // two triangles spanning x 0..1 (along) and y -1..1 (across)
    quad.setAttribute('position', new THREE.BufferAttribute(new Float32Array([
      0, -1, 0, 1, -1, 0, 1, 1, 0,
      0, -1, 0, 1, 1, 0, 0, 1, 0
    ]), 3));

    const geo = new THREE.InstancedBufferGeometry();
    geo.index = quad.index;
    geo.attributes.position = quad.attributes.position;
    const start = new Float32Array(N * 3);
    const end = new Float32Array(N * 3);
    const idx = new Float32Array(N);
    for (let i = 0; i < N; i++) idx[i] = i;
    const aStart = new THREE.InstancedBufferAttribute(start, 3);
    const aEnd = new THREE.InstancedBufferAttribute(end, 3);
    aStart.setUsage(THREE.DynamicDrawUsage);
    aEnd.setUsage(THREE.DynamicDrawUsage);
    geo.setAttribute('aStart', aStart);
    geo.setAttribute('aEnd', aEnd);
    geo.setAttribute('aIdx', new THREE.InstancedBufferAttribute(idx, 1));
    geo.instanceCount = N;
    geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e3);

    const uni = Object.assign({}, this._shared, {
      uHead: { value: 0 },
      uCount: { value: N },
      uTauSamples: { value: 100 },
      uAgeBias: { value: 0 },
      uHoldHead: { value: 0 },
      uHeadLen: { value: HEAD_SAMPLES },
      uTrailCore: { value: widths[0] },
      uTrailHalo: { value: widths[1] },
      uHeadCore: { value: widths[2] },
      uHeadHalo: { value: widths[3] },
      uOpacity: { value: opacity },
      uBaseLum: { value: BASE_LUM },
      uColor: { value: new THREE.Color().setRGB(color[0], color[1], color[2], THREE.LinearSRGBColorSpace) },
      uConverge: { value: this._layout.converge ? CONVERGE_PX : 0 },
      uCore: { value: P31_CORE }
    });
    const mat = new THREE.ShaderMaterial({
      uniforms: uni,
      vertexShader: traceVertex,
      fragmentShader: traceFragment,
      blending: THREE.AdditiveBlending,
      transparent: true, depthWrite: false, depthTest: false
    });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.frustumCulled = false;
    mesh.renderOrder = 10;
    // The fat line is expanded in PIXELS, so the shader needs the real
    // drawing buffer, and onBeforeRender on an object is the only place a
    // visualizer is handed the renderer. One trace carries the hook for all
    // of them, since they share the uniform holder.
    if (!this._hooked) {
      const self = this;
      mesh.onBeforeRender = function (renderer) { self._onRender(renderer); };
      this._hooked = true;
    }
    scene.add(mesh);

    return {
      mesh: mesh, geo: geo, quad: quad, mat: mat, uni: uni,
      start: start, end: end, aStart: aStart, aEnd: aEnd,
      n: N, head: 0, persistMs: persistMs, widths: widths,
      last: new THREE.Vector3(), primed: false
    };
  }

  _makeGrids(scene) {
    this._grids = [];
    const size = GRID_EXTENT * 2;
    const planes = [
      { rot: [0, 0, 0] },                       // XY
      { rot: [-Math.PI / 2, 0, 0] },            // XZ
      { rot: [0, Math.PI / 2, 0] }              // YZ
    ];
    for (const p of planes) {
      const uni = Object.assign({}, this._shared, {
        uColor: { value: new THREE.Color().setRGB(0.30, 0.85, 0.42, THREE.LinearSRGBColorSpace) },
        uLevel: { value: GRID_LEVEL },
        uDiv: { value: GRID_DIV },
        uExtent: { value: GRID_EXTENT },
        uFocalPx: { value: 520 }
      });
      const mat = new THREE.ShaderMaterial({
        uniforms: uni, vertexShader: gridVertex, fragmentShader: gridFragment,
        blending: THREE.AdditiveBlending, transparent: true,
        depthWrite: false, depthTest: false, side: THREE.DoubleSide
      });
      const mesh = new THREE.Mesh(new THREE.PlaneGeometry(size, size), mat);
      mesh.rotation.set(p.rot[0], p.rot[1], p.rot[2]);
      mesh.frustumCulled = false;
      mesh.renderOrder = -10;
      scene.add(mesh);
      this._grids.push({ mesh: mesh, mat: mat, uni: uni });
    }
  }

  // ------------------------------------------------------------ the curve --
  // SMALL WHOLE NUMBERS, never all equal, and at least two of them coprime.
  // Three multiples of a common factor draw the same figure as the reduced
  // triplet at a slower rate - 2,4,6 is 1,2,3 - so without the coprime rule a
  // third of the draws are a figure you have already seen, running slower.
  _pickFreqs(progress) {
    if (this._forcedABC) {
      this._abc = this._forcedABC.slice();
      this._abc2 = [this._abc[0], this._abc[1] + 1, this._abc[2]];
      return;
    }
    const mid = Math.sin(Math.PI * clamp01(progress));
    const pool = mid > 0.45 ? FREQ_POOL_MID : FREQ_POOL_EARLY;
    // ALL THREE DIFFERENT, not merely "not all equal". Two equal frequencies
    // put that pair of axes in a fixed phase relation, so the curve lies on an
    // elliptic cylinder and reads as a tilted ellipse with a wobble - a plane
    // figure, which is the one thing a 3D scope must not be. Counted over
    // 20 000 draws, the looser rule let 46.8% of triplets through with a
    // repeat, and that is what was on screen.
    let a = 1, b = 2, c = 3;
    for (let k = 0; k < 40; k++) {
      a = pool[(Math.random() * pool.length) | 0];
      b = pool[(Math.random() * pool.length) | 0];
      c = pool[(Math.random() * pool.length) | 0];
      if (a === b || b === c || a === c) continue;
      if (gcd(a, b) === 1 || gcd(b, c) === 1 || gcd(a, c) === 1) break;
    }
    if (a === b || b === c || a === c) { a = 1; b = 2; c = 3; }
    this._abc = [a, b, c];
    // THE SECOND CHANNEL IS RELATED, NOT INDEPENDENT. One axis a whole step
    // up: the two figures then turn inside one another with a ratio the eye
    // can follow, which two unrelated triplets never do.
    this._abc2 = [a, b + 1, c];
  }

  // ---------------------------------------------------------- the camera --
  // On a phrase the camera jumps to the angle that lines one of the figure's
  // own axes up with the eye - the view in which it is most symmetric, and the
  // one that makes a new triplet legible before it starts turning again.
  _snapAngle() {
    const axis = (this._phraseCount || 0) % 3;
    return axis === 0 ? 0 : (axis === 1 ? Math.PI / 2 : Math.PI / 4);
  }

  _placeCamera(dt, bpm) {
    if (!this._freeze) {
      this._orbit += (dt / (ORBIT_BEATS * 60 / bpm)) * Math.PI * 2;
      if (this._orbitTarget != null) {
        let d = this._orbitTarget - this._orbit;
        while (d > Math.PI) d -= Math.PI * 2;
        while (d < -Math.PI) d += Math.PI * 2;
        this._orbit += d * Math.min(1, dt * 3.5);
        if (Math.abs(d) < 0.02) this._orbitTarget = null;
      }
      this._tiltPhase += dt / TILT_PERIOD_S * Math.PI * 2;
    }
    const tilt = Math.sin(this._tiltPhase) * TILT_DEG * Math.PI / 180;
    const c = this._camera;
    const ct = Math.cos(tilt);
    c.position.set(
      Math.sin(this._orbit) * ORBIT_RADIUS * ct,
      Math.sin(tilt) * ORBIT_RADIUS,
      Math.cos(this._orbit) * ORBIT_RADIUS * ct
    );
    // Aim below the origin, so the figure sits above the middle of the frame
    // and the cover has the centre.
    const halfH = ORBIT_RADIUS * Math.tan((c.fov || 68) * Math.PI / 360);
    this._aim.set(0, -AIM_DROP * halfH, 0);
    c.lookAt(this._aim);
  }

  // --------------------------------------------------------------- update --
  update(deltaTime, profile) {
    this._paused = profile.playbackState !== 'PLAYING';
    const dt = Math.min(deltaTime, 0.1);
    this._time += dt;
    if (!this._paused) {
      const director = profile.director || {};
      const bands = director.bands || { low: 0, mid: 0, high: 0 };
      const k = 1 - Math.exp(-dt / 0.22);
      this._lowSmooth += (clamp01(bands.low) - this._lowSmooth) * k;
      this._midSmooth += (clamp01(bands.mid) - this._midSmooth) * k;
      this._highSmooth += (clamp01(bands.high) - this._highSmooth) * k;
    }

    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || 120;
    const barS = 240 / bpm;

    // one beat counter, real or free-running, exactly as Airframe does it
    const realBeats = (profile.beatIndex != null && profile.beatPhase != null)
      ? profile.beatIndex + profile.beatPhase : null;
    if (!this._paused) {
      if (realBeats != null) {
        if (this._lastRealBeats == null) this._beatOffset = Math.round(this._beats - realBeats);
        this._beats = realBeats + this._beatOffset;
        this._lastRealBeats = realBeats;
      } else {
        this._lastRealBeats = null;
        this._beats += dt * bpm / 60;
      }
    }
    const phrase = Math.floor(this._beats / 16);
    if (phrase !== this._lastPhrase) {
      if (this._lastPhrase != null) {
        this._phraseCount = (this._phraseCount || 0) + 1;
        this._pickFreqs(profile.duration > 0 ? clamp01(profile.progress) : 0.5);
        this._orbitTarget = this._snapAngle();
      }
      this._lastPhrase = phrase;
    }

    if (profile.artworkPalette !== this._lastPaletteRef) {
      this._lastPaletteRef = profile.artworkPalette;
      const vivid = profile.artworkPalette && profile.artworkPalette.vividColors;
      if (vivid && vivid.length && this._traces.length > 1) {
        this._traces[1].uni.uColor.value.set(vivid[0]);
      }
    }

    // --- drops: the persistence opens up to a long exposure ---
    const ev = (profile.director || {}).nextEvent;
    if (ev && (ev.type === 'drop' || ev.type === 'rise')) {
      const until = ev.msUntil / 1000;
      if (until > 0 && until <= 0.5) this._pendingDropAt = this._time + until;
    }
    if (this._pendingDropAt != null && this._time >= this._pendingDropAt) {
      this._pendingDropAt = null;
      this._dropUntil = this._time + DROP_BARS * barS;
    }
    const dropping = this._time < this._dropUntil;

    // --- the phases drift: this, and only this, is the precession ---
    if (!this._paused) {
      const drift = PHI_DRIFT * lerp(0.5, PHI_DRIFT_MID, this._midSmooth) * (dt * 60);
      this._phi[2] += drift;
      this._phi[1] += drift * 0.23;
    }

    // --- advance the spot and lay down samples ---
    const amp = AMP * lerp(1 - AMP_LOW, 1 + AMP_LOW, this._lowSmooth);
    if (!this._paused) {
      const perBar = SAMPLES_PER_BAR;
      const dTurns = dt / barS;                      // fraction of a bar
      const want = dTurns * perBar;
      this._sampleDebt = (this._sampleDebt || 0) + want;
      let n = Math.floor(this._sampleDebt);
      this._sampleDebt -= n;
      n = Math.min(n, 240);                          // a long frame cannot cascade
      const dtPer = (2 * Math.PI) / perBar;
      for (let i = 0; i < n; i++) {
        this._t += dtPer;
        for (let k = 0; k < this._traces.length; k++) {
          const tr = this._traces[k];
          const abc = k === 0 ? this._abc : this._abc2;
          const a = k === 0 ? amp : amp * 0.62;
          this._push(tr, this._t, abc, a);
        }
      }
    }

    // --- uniforms ---
    // While paused the spot stops laying down samples, so the trail's ages
    // stop advancing with it. The bias is what keeps the screen forgetting.
    if (this._paused) this._pauseAge += dt * (SAMPLES_PER_BAR / barS);
    else this._pauseAge = 0;
    const holdHead = this._paused ? clamp01(this._pauseAge / 220) * 0.9 : 0;

    const persistBase = this._forcedPersist != null ? this._forcedPersist : null;
    for (let k = 0; k < this._traces.length; k++) {
      const tr = this._traces[k];
      const ms = persistBase != null ? persistBase
        : (dropping ? PERSIST_MS_DROP : tr.persistMs);
      // The decay is written in SAMPLES because that is what the shader can
      // count; samples per second is the traversal rate, so the constant
      // follows the tempo without anything else having to know about it.
      const perSec = SAMPLES_PER_BAR / barS;
      tr.uni.uTauSamples.value = Math.max(2, (ms / 1000) * perSec);
      tr.uni.uHead.value = (tr.head - 1 + tr.n) % tr.n;
      tr.uni.uAgeBias.value = this._pauseAge;
      tr.uni.uHoldHead.value = holdHead;
      tr.uni.uHeadHalo.value = tr.widths[3] + (k === 0 ? HEAD_PX_HIGH * this._highSmooth : 0);
      tr.aStart.needsUpdate = true;
      tr.aEnd.needsUpdate = true;
    }

    this._placeCamera(this._paused ? 0 : dt, bpm);
  }

  // Writes one sample into a ring. Segment i runs from sample i to sample
  // i+1, so taking a sample completes the PREVIOUS segment and opens this one.
  _push(tr, t, abc, amp) {
    const i = tr.head;
    const s = tr.start, e = tr.end;
    const x = amp * Math.sin(abc[0] * t + this._phi[0]);
    const y = amp * Math.sin(abc[1] * t + this._phi[1]);
    const z = amp * Math.sin(abc[2] * t + this._phi[2]);
    s[i * 3] = x; s[i * 3 + 1] = y; s[i * 3 + 2] = z;
    const p = (i - 1 + tr.n) % tr.n;
    e[p * 3] = x; e[p * 3 + 1] = y; e[p * 3 + 2] = z;
    if (!tr.primed) {
      // The first sample has no predecessor; give the opening segment zero
      // length rather than a chord from the origin.
      e[i * 3] = x; e[i * 3 + 1] = y; e[i * 3 + 2] = z;
      tr.primed = true;
    }
    tr.head = (i + 1) % tr.n;
  }

  _onRender(renderer) {
    renderer.getDrawingBufferSize(_size);
    this._shared.uHalfRes.value.set(_size.x * 0.5, _size.y * 0.5);
    if (this._grids) {
      const fov = (this._camera.fov || 68) * Math.PI / 360;
      const focal = (_size.y * 0.5) / Math.tan(fov);
      for (const g of this._grids) g.uni.uFocalPx.value = focal;
    }
  }

  dispose() {
    for (const tr of this._traces) {
      tr.mesh.onBeforeRender = null;
      this._scene.remove(tr.mesh);
      tr.geo.dispose();
      tr.quad.dispose();
      tr.mat.dispose();
    }
    for (const g of this._grids || []) {
      this._scene.remove(g.mesh);
      g.mesh.geometry.dispose();
      g.mat.dispose();
    }
    this._traces = [];
    this._grids = [];
  }
}

const _size = new THREE.Vector2();
